import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { ArrowLeft, Timer, Zap, Brain } from 'lucide-react';

interface PuzzleCard {
  id: number;
  symbol: string;
  flipped: boolean;
  matched: boolean;
}

const SYMBOLS = ['🌳', '🌸', '🦋', '🌙', '⭐', '🍀', '🌺', '🐝'];

export default function MemoryPuzzle() {
  const navigate = useNavigate();
  const { addEnergy, completeLevel, unlockLevel } = useGameStore();
  const [cards, setCards] = useState<PuzzleCard[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matchedPairs, setMatchedPairs] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [gameState, setGameState] = useState<'ready' | 'preview' | 'playing' | 'complete'>('ready');
  const [timeLeft, setTimeLeft] = useState(60);
  const [startTime, setStartTime] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const totalPairs = 8;

  const initializeCards = useCallback(() => {
    const pairs = [...SYMBOLS, ...SYMBOLS];
    const shuffled = pairs.sort(() => Math.random() - 0.5);
    return shuffled.map((symbol, index) => ({
      id: index,
      symbol,
      flipped: false,
      matched: false,
    }));
  }, []);

  const startGame = () => {
    const newCards = initializeCards();
    setCards(newCards);
    setMatchedPairs(0);
    setAttempts(0);
    setFlippedCards([]);
    setTimeLeft(60);
    setGameState('preview');

    // Show all cards briefly for memorization
    setCards(newCards.map(c => ({ ...c, flipped: true })));
    setTimeout(() => {
      setCards(newCards.map(c => ({ ...c, flipped: false })));
      setGameState('playing');
      setStartTime(Date.now());
    }, 3000);
  };

  useEffect(() => {
    if (gameState === 'playing') {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setGameState('complete');
            setShowResult(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameState]);

  useEffect(() => {
    if (flippedCards.length === 2) {
      setAttempts(a => a + 1);
      const [first, second] = flippedCards;
      if (cards[first].symbol === cards[second].symbol) {
        // Match found
        setTimeout(() => {
          setCards(prev => prev.map((c, i) =>
            i === first || i === second ? { ...c, matched: true } : c
          ));
          setMatchedPairs(m => {
            const newM = m + 1;
            if (newM >= totalPairs) {
              setGameState('complete');
              setShowResult(true);
            }
            return newM;
          });
          setFlippedCards([]);
        }, 300);
      } else {
        // No match - flip back
        setTimeout(() => {
          setCards(prev => prev.map((c, i) =>
            i === first || i === second ? { ...c, flipped: false } : c
          ));
          setFlippedCards([]);
        }, 800);
      }
    }
  }, [flippedCards, cards]);

  const handleCardClick = (index: number) => {
    if (gameState !== 'playing') return;
    if (flippedCards.length >= 2) return;
    if (cards[index].flipped || cards[index].matched) return;

    setCards(prev => prev.map((c, i) => i === index ? { ...c, flipped: true } : c));
    setFlippedCards(prev => [...prev, index]);
  };

  const handleComplete = () => {
    const timeTaken = (Date.now() - startTime) / 1000;
    const accuracy = attempts > 0 ? (matchedPairs / attempts) * 100 : 0;
    const avgReaction = attempts > 0 ? (timeTaken * 1000) / attempts : 0;

    const energyEarned = matchedPairs * 5 + Math.floor(accuracy / 5);
    addEnergy(energyEarned);

    completeLevel({
      levelId: 'memory-puzzle',
      levelName: '记忆拼图',
      score: matchedPairs * 10 + Math.max(0, (60 - Math.floor(timeTaken)) * 2),
      accuracy: Math.min(100, accuracy),
      reactionTime: avgReaction,
      completedAt: new Date().toISOString(),
      contrast: 1,
      speed: 1,
    });

    unlockLevel('eye-exercise');
    navigate('/report');
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#0a1a10] via-[#122a1a] to-[#1a3525] relative overflow-hidden">
      {/* Header */}
      <header className="relative z-10 px-4 pt-6 pb-2">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <button onClick={() => navigate('/')} className="text-white/60 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-white">🧩 记忆拼图</h1>
          <div className="flex items-center gap-2">
            <Timer className="w-4 h-4 text-white/60" />
            <span className={`text-sm font-bold ${timeLeft <= 10 ? 'text-red-400' : 'text-white/80'}`}>{timeLeft}s</span>
          </div>
        </div>
        <div className="max-w-lg mx-auto mt-3 flex items-center justify-between text-sm">
          <span className="text-[#7dffa8]">配对: {matchedPairs}/{totalPairs}</span>
          <span className="text-white/50">尝试: {attempts}</span>
        </div>
      </header>

      {/* Game Area */}
      <div className="relative z-10 px-4 mt-6">
        <div className="max-w-lg mx-auto">
          {gameState === 'ready' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <Brain className="w-16 h-16 mx-auto mb-6 text-[#c084fc]" />
              <h2 className="text-xl font-bold text-white mb-3">记忆拼图</h2>
              <p className="text-white/60 text-sm mb-2 max-w-xs mx-auto">
                卡片会先全部展示3秒供你记忆位置。
              </p>
              <p className="text-white/60 text-sm mb-8 max-w-xs mx-auto">
                然后翻回背面，找出所有配对的图案！强化视觉记忆能力。
              </p>
              <button onClick={startGame} className="btn-primary text-lg">
                <Zap className="w-5 h-5 inline mr-2" />开始记忆
              </button>
            </motion.div>
          )}

          {gameState === 'preview' && (
            <div className="text-center py-4">
              <motion.p
                className="text-[#fbbf24] font-bold text-lg mb-4"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                👀 记住卡片位置！
              </motion.p>
            </div>
          )}

          {(gameState === 'preview' || gameState === 'playing') && (
            <div className="grid grid-cols-4 gap-2 max-w-[350px] mx-auto">
              {cards.map((card, index) => (
                <motion.button
                  key={card.id}
                  className={`aspect-square rounded-xl flex items-center justify-center text-2xl border-2 transition-colors ${
                    card.matched 
                      ? 'border-[#7dffa8]/50 bg-[#7dffa8]/10' 
                      : card.flipped 
                        ? 'border-[#fbbf24]/50 bg-white/10' 
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}
                  onClick={() => handleCardClick(index)}
                  whileTap={{ scale: 0.9 }}
                  initial={{ rotateY: 0 }}
                  animate={{ rotateY: card.flipped || card.matched ? 0 : 180 }}
                  transition={{ duration: 0.3 }}
                >
                  {(card.flipped || card.matched) ? (
                    <span>{card.symbol}</span>
                  ) : (
                    <span className="text-white/20">?</span>
                  )}
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Result Modal */}
      <AnimatePresence>
        {showResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="game-card p-6 max-w-sm w-full text-center"
            >
              <div className="text-4xl mb-4">🧠</div>
              <h2 className="text-xl font-bold text-white mb-4">记忆挑战完成！</h2>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">配对数</p>
                  <p className="text-lg font-bold text-[#7dffa8]">{matchedPairs}/{totalPairs}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">尝试次数</p>
                  <p className="text-lg font-bold text-[#fbbf24]">{attempts}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">准确率</p>
                  <p className="text-lg font-bold text-[#60a5fa]">
                    {attempts > 0 ? Math.round((matchedPairs / attempts) * 100) : 0}%
                  </p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">获得能量</p>
                  <p className="text-lg font-bold text-[#fbbf24]">
                    +{matchedPairs * 5 + Math.floor((attempts > 0 ? (matchedPairs / attempts) * 100 : 0) / 5)}
                  </p>
                </div>
              </div>
              <button onClick={handleComplete} className="btn-primary w-full">
                查看训练报告 📊
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
